import React from "react";

import moment from "moment";

import { FIELD_TYPES, REPORT_DATE_FIELDS, INJECTION_CHARS } from "globals/constants";

import { getDateTimeLocale } from "./date";

export const getMappingData = availableColumns => {
	const mappingData = {};

	availableColumns.forEach(column => {
		const hasBrackets = column.columnHeader?.startsWith("[") && column.columnHeader?.endsWith("]");
		if ([ FIELD_TYPES.DATE, FIELD_TYPES.DATETIME ].includes(column.type) || REPORT_DATE_FIELDS.includes(column.id)) {
			if (column.extraSettings?.enableTime) {
				mappingData[column.columnHeader] = {
					type: FIELD_TYPES.DATETIME,
				};
			} else {
				mappingData[column.columnHeader] = {
					type: FIELD_TYPES.DATE_STRING
				};
			}
			if (hasBrackets) {
				mappingData[column.columnHeader].caption = column.columnHeader;
			}
		} else if (hasBrackets) {
			mappingData[column.columnHeader] = {
				type: "string",
				caption: column.columnHeader
			};
		}
	});

	return mappingData;
};

export function* generateDataObjects (data, headers) {
	const rows = JSON.parse(data);// getAllRows(data);
	yield headers.map(header => JSON.stringify(header.columnHeader));
	const dateTimeFormat = getDateTimeLocale();

	for (const row of rows) {
		let record = [];
		for (const header of headers) {
			record.push(getCellData(row, header, dateTimeFormat));
		}
		yield record;
	}
}

const createReportData = (data, allColumns) => {
	const availableColumns = allColumns
		.filter(column => column.keys && column.columnHeader);

	return Array.from(generateDataObjects(data, availableColumns))
		.join("\n");
};

export const exportReportAsCSV = async props => {
	const {
		download,
		selectedColumnHeaders,
		filename,
		onError,
		submissionType,
		onComplete
	} = props;

	const fields = (selectedColumnHeaders || [])
		.reduce((acc, columnHeader) => {
			acc.push(...columnHeader.keys);

			return acc;
		}, [])
		.filter((item, k, arr) => arr.indexOf(item) === k)
		.join(",");

	const payload = {
		submissionType,
		fields,
		filename
	};

	try {
		const blob = await download(payload, true);
		if (blob) {
			const text = await blob.text();
			createCSVDownload(filename, text, selectedColumnHeaders);
		}
	}
	catch (e) {
		onError("An error occured while downloading CSV", e);
	}


	onComplete();
};

export const createCSVDownload = (filename, text, allColumns) => {
	const newData = createReportData(text, allColumns);
	const newBlob = new Blob([ "\ufeff", newData ], { type: "text/csv; charset=utf-8" });
	const fileUrl = URL.createObjectURL(newBlob);

	const anchor = document.createElement("a");
	document.body.appendChild(anchor);

	anchor.href = fileUrl;
	anchor.download = filename;
	anchor.click();

	//window.URL.revokeObjectURL(fileUrl);
};

export const escapeSpecialChars = obj => {
	if (typeof obj !== "object" || obj === null) {return obj;}

	const result = { ...obj };
	Object.keys(result).forEach(key => {
		const value = result[key];
		if (typeof value === "string" && /^[=+\-@]/.test(value) && value !== "--") {
			result[key] = " " + value;
		}
	});

	return result;
};

export const createJSONBlobURL = (text, allColumns) => {
	const rawData = JSON.parse(text);
	const processedData = rawData.map(escapeSpecialChars);

	const dataMapping = allColumns
		.filter(column => column.keys && column.columnHeader)
		.map(column => column.columnHeader)
		.reduce((a, v) => {
			a[v] = {
				//visible: a[v]?.visible || !!a[v]
				type: "string"
			};

			return a;
		}, {});

	const newBlob = new Blob([ "\ufeff", JSON.stringify(processedData) ], { type: "application/json; charset=utf-8" });

	return [
		URL.createObjectURL(newBlob),
		{
			...dataMapping,
			...getMappingData(allColumns)
		}
	];
};

export const getCellData = (row, header, dateTimeFormat) => {
	if(!row || !row[header?.columnHeader] || row[header?.columnHeader] === "") {
		return "";
	} else if ([ FIELD_TYPES.DATE, FIELD_TYPES.DATETIME ].includes(header.type) || REPORT_DATE_FIELDS.includes(header.id)) {
		if (header.extraSettings?.enableTime) {
			return moment(row[header.columnHeader]).format(`${dateTimeFormat.date} ${dateTimeFormat.time}`);
		} else {
			return moment(row[header.columnHeader]).format(dateTimeFormat.date);
		}
	} else if ([ FIELD_TYPES.TEXT, FIELD_TYPES.TEXTAREA, FIELD_TYPES.RICH_TEXT ].includes(header.type)) {
		if (INJECTION_CHARS.includes(row[header.columnHeader].charAt(0))) {
			return "\"\u200B" + row[header.columnHeader].replace(/"/g, "\"\"") + "\"";
		}
	} else if (header.type === FIELD_TYPES.FUNDING_PROGRAM) {
		const val = String(row[header.columnHeader] || "");
		if (val === "") return "";

		return `"${val.replace(/"/g, '""')}"`;
	}

	// The replace is to escape double quotes that were turned to \" by JSON.stringify
	// to "" instead. This is to make sure that the CSV is properly formatted when opened in Excel.
	return JSON.stringify(row[header.columnHeader])
		.replace(/\\"/g, "\"\"")
		.replace("\\\\", "\\");
};
